import { Text } from '@/components/ui/text'
import { CommanderScore } from '@components/SetupCommanderScore'
import { CommunityScore } from '@components/SetupCommunityScore'
import SetupDuration from '@components/SetupDuration'
import SetupPotential from '@components/SetupPotential'
import SetupRRR from '@components/SetupRRR'
import type { StageEnum, Tables } from '@hvf/contracts'
import { Star } from 'lucide-solid'
import { type ParentProps, Show } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Box, Divider, Flex, Grid } from 'styled-system/jsx'
import SpecificationBadge from '../../SpecificationBadge'
import { stageColor } from '../../StageBadge'
import { useSingle } from '../state'

const HAIR = 'var(--colors-editorial-hair)'

const GridInner = (props: ParentProps<{ title: string; padding?: string; shrink?: boolean }>) => {
  return (
    <Box
      p={props.padding || '10px 14px'}
      minWidth={props.shrink ? 0 : undefined}
      class={css({
        '& + &': {
          borderLeft: `1px solid ${HAIR}`,
        },
      })}
      gap='5px'
      display='grid'
      justifyItems='center'
      alignItems='center'
    >
      <Text
        fontSize='9px'
        letterSpacing='.14em'
        textTransform='uppercase'
        color='editorialInk3'
        fontWeight='600'
      >
        {props.title}
      </Text>
      {props.children}
    </Box>
  )
}

const StatusDot = (props: { stage?: StageEnum | null }) => {
  const label = () => (props.stage || 'unspecified').replaceAll('_', ' ').replaceAll('-', ' ')

  return (
    <Flex alignItems='center' gap='7px'>
      <Box
        w='8px'
        h='8px'
        borderRadius='full'
        flexShrink={0}
        style={{ background: stageColor(props.stage) }}
      />
      <Text fontSize='13px' fontWeight='700' color='editorialInk' textTransform='capitalize'>
        {label()}
      </Text>
    </Flex>
  )
}

export default function Details(props: { setup: Tables<'setup'> }) {
  const { takenQuery } = useService(useSingle)()

  const score = () => takenQuery.data?.score

  const entry = () =>
    props.setup.position === 'long' ? props.setup.high_3_price : props.setup.low_3_price
  const stop = () =>
    props.setup.position === 'long' ? props.setup.low_3_price : props.setup.high_3_price

  return (
    <Grid
      bg='editorialSoft'
      border={`1px solid ${HAIR}`}
      gap='0'
      borderRadius='14px'
    >
      <Grid gridTemplateColumns='1.5fr 1fr' gap='0'>
        <GridInner title='status' padding='11px 14px'>
          <StatusDot stage={props.setup?.stage} />
        </GridInner>
        <GridInner title='Risk Reward Ratio' padding='11px 14px'>
          <SetupRRR setup={props.setup} fontSize='21px' />
        </GridInner>
      </Grid>
      <Divider borderColor={HAIR} />
      <Grid gridTemplateColumns='1fr 1fr 1fr' gap='0'>
        <GridInner title='entry'>
          <Text fontSize='15px' fontWeight='700' color='editorialInk'>{entry()}</Text>
        </GridInner>
        <GridInner title='target'>
          <Text fontSize='15px' fontWeight='700' color='editorialAccentDeep'>{props.setup.target}</Text>
        </GridInner>
        <GridInner title='stop'>
          <Text fontSize='15px' fontWeight='700' color='editorialShort'>{stop()}</Text>
        </GridInner>
      </Grid>
      <Divider borderColor={HAIR} />
      <Grid gridTemplateColumns='minmax(0, 1.5fr) auto auto' gap='0'>
        <GridInner title='pattern spec' shrink>
          <SpecificationBadge patternSpecification={props.setup?.pattern_specification} wrap />
        </GridInner>
        <GridInner title='timeframe'>
          <SetupDuration setup={props.setup} />
        </GridInner>
        <GridInner title='attributes'>
          <SetupPotential setup={props.setup} size='26px' />
        </GridInner>
      </Grid>
      <Divider borderColor={HAIR} />
      <Grid gridTemplateColumns='1fr 1fr 1fr' gap='0'>
        <GridInner title='PHQ'>
          <CommanderScore setupId={props.setup?.id} />
        </GridInner>
        <GridInner title='Community'>
          <CommunityScore
            setup={props.setup}
          />
        </GridInner>
        <GridInner title='Your score'>
          <Show
            when={score()}
            fallback={<Text fontSize='14.5px' fontWeight='700' color='editorialInk3'>-</Text>}
          >
            <Flex alignItems='center' gap='6px'>
              <Star size={15} fill='var(--colors-editorial-star)' stroke='var(--colors-editorial-star)' />
              <Text fontSize='14.5px' fontWeight='700' color='editorialInk'>{score()}</Text>
            </Flex>
          </Show>
        </GridInner>
      </Grid>
    </Grid>
  )
}
